import { Injectable } from '@angular/core'
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker'
import { interval, filter, switchMap } from 'rxjs'
import { TranslateService } from '@ngx-translate/core'
import { environment } from '../../../config/environments/environment'

@Injectable({
  providedIn: 'root',
})
export class UpdateService {
  private updateAvailable = false
  private readonly UPDATE_DISMISSED_KEY = 'yabi_update_dismissed'
  private isUpdating = false

  constructor(private swUpdate: SwUpdate, private translate: TranslateService) {
    this.setupErrorHandling()
  }

  public checkForUpdates(): void {
    if (this.swUpdate.isEnabled) {
      // Check for updates every 60 seconds (less frequent)
      interval(60000)
        .pipe(
          switchMap(() => {
            return this.swUpdate.checkForUpdate()
          })
        )
        .subscribe((_hasUpdate) => {/* noop */})

      // Listen for version ready events
      this.swUpdate.versionUpdates.pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')).subscribe((_evt) => {
        this.updateAvailable = true
        this.showUpdateNotification()
      })

      // Initial check after app loads
      setTimeout(() => {
        this.swUpdate.checkForUpdate().catch(() => {
          // Silently handle errors
        })
      }, 10000) // Wait 10 seconds after app starts
    }
  }

  /**
   * Force check for updates (can be called from browser console for testing)
   * Usage: window.updateService.forceCheckForUpdates()
   */
  public forceCheckForUpdates(): void {
    if (this.swUpdate.isEnabled) {
      this.swUpdate
        .checkForUpdate()
        .then((hasUpdate) => {
          if (hasUpdate) {
            this.updateAvailable = true
            this.showUpdateNotification()
          }
        })
        .catch(() => {
          // Silently handle errors
        })
    }
  }

  /**
   * Expose this service to window for debugging
   */
  public exposeToWindow(): void {
    ;(window as unknown as { updateService: UpdateService }).updateService = this
  }

  /**
   * Test the notification UI (for debugging)
   * Usage: window.updateService.testNotification()
   */
  public testNotification(): void {
    const testVersionId = `test-${Date.now()}`

    // Remove any existing notifications first
    const existingNotifications = document.querySelectorAll('[data-update-notification="persistent"]')
    existingNotifications.forEach((el) => el.remove())

    // Create a test notification that just shows UI, doesn't actually update
    const notification = this.createTestNotification(testVersionId)
    notification.setAttribute('data-update-notification', 'persistent')
    document.body.appendChild(notification)
  }

  /**
   * Clear dismissed updates from localStorage (for debugging)
   * Usage: window.updateService.clearDismissedUpdates()
   */
  public clearDismissedUpdates(): void {
    localStorage.removeItem(this.UPDATE_DISMISSED_KEY)
  }

  /**
   * Get service worker registration info
   */
  public async getServiceWorkerInfo(): Promise<Record<string, unknown>> {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.getRegistration()
        const info = {
          active: !!registration?.active,
          waiting: !!registration?.waiting,
          installing: !!registration?.installing,
          scope: registration?.scope,
          updatefound: registration ? 'updatefound event can be listened to' : 'no registration',
          swUpdateEnabled: this.swUpdate.isEnabled,
          updateAvailable: this.updateAvailable,
        }

        return info
      } catch (error) {
        console.error('Error getting service worker info:', error)
        return { error: String(error) }
      }
    } else {
      return { supported: false }
    }
  }

  private showUpdateNotification(): void {
    // Check if user has dismissed this version update
    // Use a more specific approach based on service worker version hash instead of just date
    const dismissedVersion = localStorage.getItem(this.UPDATE_DISMISSED_KEY)

    // Get current service worker version info if available
    let currentVersionId = `v${environment.version || '1.4.20'}-${new Date().toISOString().split('T')[0]}` // Use environment version + date

    // Try to get version from meta tag (set during build)
    const versionMeta = document.querySelector('meta[name="version"]')?.getAttribute('content')
    if (versionMeta && versionMeta !== '{{BUILD_VERSION}}') {
      currentVersionId = versionMeta
    }

    // If service worker is enabled, we can also check for more specific version info
    if (this.swUpdate.isEnabled) {
      // The service worker itself will tell us about version updates
      // so we can use a timestamp-based approach for the dismissal
      currentVersionId = currentVersionId + '-' + Date.now().toString().substring(0, 10)
    }

    if (dismissedVersion === currentVersionId) {
      return // User has dismissed this update
    }

    // Remove any existing notifications first
    const existingNotifications = document.querySelectorAll('[data-update-notification="persistent"]')
    existingNotifications.forEach((el) => el.remove())

    // Create a user-friendly notification that persists
    const notification = this.createUpdateNotification(currentVersionId)
    notification.setAttribute('data-update-notification', 'persistent')
    document.body.appendChild(notification)
  }

  private createUpdateNotification(currentVersionId: string): HTMLElement {
    // Add CSS animations if not already present
    if (!document.querySelector('#update-notification-styles')) {
      const style = document.createElement('style')
      style.id = 'update-notification-styles'
      style.textContent = `
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes slideOut {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }
      `
      document.head.appendChild(style)
    }

    const notification = document.createElement('div')
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, #9578d3 0%, #8a6cc7 100%);
      color: white;
      padding: 20px 24px;
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(149, 120, 211, 0.4);
      z-index: 10000;
      max-width: 380px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Assistant', sans-serif;
      font-size: 14px;
      line-height: 1.5;
      animation: slideIn 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
      border: 1px solid rgba(255, 255, 255, 0.2);
      backdrop-filter: blur(10px);
    `

    // Add notification header with icon
    const header = document.createElement('div')
    header.style.cssText = `
      display: flex;
      align-items: center;
      margin-bottom: 12px;
      gap: 10px;
    `

    const iconContainer = document.createElement('div')
    iconContainer.style.cssText = `
      width: 32px;
      height: 32px;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      animation: pulse 2s infinite;
    `
    iconContainer.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M21 12c0 1-1 1-1 1s-1 0-1-1 1-1 1-1 1 0 1 1"/>
        <path d="M16 12c0 1-1 1-1 1s-1 0-1-1 1-1 1-1 1 0 1 1"/>
        <path d="M11 12c0 1-1 1-1 1s-1 0-1-1 1-1 1-1 1 0 1 1"/>
        <path d="M6 12c0 1-1 1-1 1s-1 0-1-1 1-1 1-1 1 0 1 1"/>
        <circle cx="12" cy="12" r="10"/>
      </svg>
    `

    const headerText = document.createElement('div')
    headerText.style.cssText = `
      flex: 1;
      font-weight: 600;
      font-size: 16px;
    `
    headerText.textContent = this.translate.instant('UPDATE.NEW_VERSION_AVAILABLE') || 'New Version Available!'

    header.appendChild(iconContainer)
    header.appendChild(headerText)

    const message = document.createElement('div')
    message.innerHTML = `
      <div style="color: rgba(255, 255, 255, 0.9); font-size: 13px; line-height: 1.4; margin-bottom: 16px;">
        ${this.translate.instant('UPDATE.RESTART_TO_UPDATE') || 'A new version of the app is ready. Click "Update Now" to restart and get the latest features and improvements.'}
      </div>
    `

    const buttonContainer = document.createElement('div')
    buttonContainer.style.display = 'flex'
    buttonContainer.style.gap = '10px'

    const updateButton = document.createElement('button')
    updateButton.textContent = this.translate.instant('UPDATE.UPDATE_NOW') || 'Update Now'
    updateButton.style.cssText = `
      background: white;
      color: #9578d3;
      border: none;
      padding: 10px 20px;
      border-radius: 8px;
      cursor: pointer;
      font-weight: 600;
      font-size: 13px;
      transition: all 0.2s ease;
      flex: 1;
      font-family: inherit;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    `
    updateButton.onmouseover = () => {
      updateButton.style.backgroundColor = '#f8f9fa'
      updateButton.style.transform = 'translateY(-1px)'
      updateButton.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)'
    }
    updateButton.onmouseout = () => {
      updateButton.style.backgroundColor = 'white'
      updateButton.style.transform = 'translateY(0)'
      updateButton.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)'
    }
    updateButton.onclick = () => this.applyUpdate()

    const laterButton = document.createElement('button')
    laterButton.textContent = this.translate.instant('UPDATE.LATER') || 'Later'
    laterButton.style.cssText = `
      background: transparent;
      color: rgba(255, 255, 255, 0.9);
      border: 1px solid rgba(255, 255, 255, 0.3);
      padding: 10px 20px;
      border-radius: 8px;
      cursor: pointer;
      font-weight: 500;
      font-size: 13px;
      transition: all 0.2s ease;
      flex: 1;
      font-family: inherit;
    `
    laterButton.onmouseover = () => {
      laterButton.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
      laterButton.style.borderColor = 'rgba(255, 255, 255, 0.5)'
      laterButton.style.transform = 'translateY(-1px)'
    }
    laterButton.onmouseout = () => {
      laterButton.style.backgroundColor = 'transparent'
      laterButton.style.borderColor = 'rgba(255, 255, 255, 0.3)'
      laterButton.style.transform = 'translateY(0)'
    }
    laterButton.onclick = () => {
      // Mark this specific version as dismissed
      this.dismissUpdate(currentVersionId)

      // Animate out
      notification.style.animation = 'slideOut 0.3s cubic-bezier(0.55, 0.055, 0.675, 0.19)'
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification)
        }
      }, 300)
    }

    const dismissButton = document.createElement('button')
    dismissButton.innerHTML = `
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    `
    dismissButton.style.cssText = `
      position: absolute;
      top: 12px;
      right: 12px;
      background: rgba(255, 255, 255, 0.1);
      color: rgba(255, 255, 255, 0.8);
      border: none;
      width: 28px;
      height: 28px;
      border-radius: 50%;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      backdrop-filter: blur(5px);
    `
    dismissButton.onmouseover = () => {
      dismissButton.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'
      dismissButton.style.color = 'white'
      dismissButton.style.transform = 'scale(1.1)'
    }
    dismissButton.onmouseout = () => {
      dismissButton.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
      dismissButton.style.color = 'rgba(255, 255, 255, 0.8)'
      dismissButton.style.transform = 'scale(1)'
    }
    dismissButton.onclick = () => {
      // Mark this specific version as dismissed
      this.dismissUpdate(currentVersionId)

      // Animate out
      notification.style.animation = 'slideOut 0.3s cubic-bezier(0.55, 0.055, 0.675, 0.19)'
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification)
        }
      }, 300)
    }

    buttonContainer.appendChild(updateButton)
    buttonContainer.appendChild(laterButton)

    notification.appendChild(header)
    notification.appendChild(message)
    notification.appendChild(buttonContainer)
    notification.appendChild(dismissButton)

    return notification
  }

  private dismissUpdate(versionId: string): void {
    // Store user's preference to dismiss this specific update version
    localStorage.setItem(this.UPDATE_DISMISSED_KEY, versionId)
  }

  private createTestNotification(_currentVersionId: string): HTMLElement {
    // Use the same styling as the real notification
    const notification = document.createElement('div')
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
      color: white;
      padding: 20px 24px;
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(255, 152, 0, 0.4);
      z-index: 10000;
      max-width: 380px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Assistant', sans-serif;
      font-size: 14px;
      line-height: 1.5;
      border: 1px solid rgba(255, 255, 255, 0.2);
      backdrop-filter: blur(10px);
    `

    const message = document.createElement('div')
    message.innerHTML = `
      <div style="font-weight: 600; font-size: 16px; margin-bottom: 8px;">
        🧪 Test Notification
      </div>
      <div style="color: rgba(255, 255, 255, 0.9); font-size: 13px; margin-bottom: 16px;">
        This is a test notification to preview the UI. No actual update will be applied.
      </div>
    `

    const closeButton = document.createElement('button')
    closeButton.textContent = 'Close'
    closeButton.style.cssText = `
      background: rgba(255, 255, 255, 0.2);
      color: white;
      border: none;
      padding: 8px 16px;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 500;
      font-size: 13px;
      transition: all 0.2s ease;
      font-family: inherit;
    `
    closeButton.onclick = () => {
      notification.remove()
    }

    notification.appendChild(message)
    notification.appendChild(closeButton)

    return notification
  }

  private applyUpdate(): void {
    if (this.swUpdate.isEnabled && this.updateAvailable) {
      // Set updating flag to suppress CSP errors
      this.isUpdating = true

      // Show loading state
      this.showLoadingNotification()

      // Clear service worker caches to prevent CSP issues
      this.clearServiceWorkerCaches()

      // Simple timeout approach - reload after 5 seconds regardless
      setTimeout(() => {
        document.location.reload()
      }, 5000)

      // Try to activate update, but don't rely on it
      this.swUpdate.activateUpdate().catch(() => {
        // If activation fails, the timeout will still reload
      })
    }
  }

  private async clearServiceWorkerCaches(): Promise<void> {
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys()

        // Clear all Angular service worker caches to ensure fresh CSP-compliant resources
        const clearPromises = cacheNames.map(async (cacheName) => {
          // Clear Angular service worker caches (they start with 'ngsw:')
          if (cacheName.startsWith('ngsw:')) {
            await caches.delete(cacheName)
          }
        })

        await Promise.all(clearPromises)

        // Also unregister and re-register the service worker to ensure clean state
        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations()
          for (const registration of registrations) {
            await registration.unregister()
          }
        }
      }
    } catch (error) {
      console.warn('Could not clear service worker caches:', error)
    }
  }

  private showLoadingNotification(): void {
    // Remove any existing notifications
    const existing = document.querySelectorAll('[data-update-notification]')
    existing.forEach((el) => el.remove())

    const notification = document.createElement('div')
    notification.setAttribute('data-update-notification', 'true')
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #4caf50;
      color: white;
      padding: 16px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      max-width: 350px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 12px;
    `

    const spinner = document.createElement('div')
    spinner.style.cssText = `
      width: 20px;
      height: 20px;
      border: 2px solid rgba(255,255,255,0.3);
      border-top: 2px solid white;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    `

    const style = document.createElement('style')
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `
    document.head.appendChild(style)

    const message = document.createElement('div')
    message.textContent = this.translate.instant('UPDATE.UPDATING') || 'Updating application...'

    notification.appendChild(spinner)
    notification.appendChild(message)
    document.body.appendChild(notification)
  }

  public forceUpdate(): void {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.checkForUpdate().then((hasUpdate) => {
        if (hasUpdate) {
          this.applyUpdate()
        }
      })
    }
  }

  /**
   * Aggressive update method for stuck scenarios
   * Usage: window.updateService.forceUpdateAggressive()
   */
  public forceUpdateAggressive(): void {
    // First try normal update detection
    if (this.swUpdate.isEnabled) {
      this.swUpdate
        .checkForUpdate()
        .then((hasUpdate) => {
          if (hasUpdate) {
            this.applyUpdate()
          } else {
            this.refreshServiceWorker()
          }
        })
        .catch((error) => {
          console.error('Error during aggressive update check:', error)
          this.refreshServiceWorker()
        })
    } else {
      location.reload()
    }
  }

  /**
   * Force refresh the service worker
   */
  private refreshServiceWorker(): void {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        if (registrations.length > 0) {
          // Unregister and re-register service worker
          const registration = registrations[0]
          registration.unregister().then(() => {
            setTimeout(() => {
              location.reload()
            }, 1000)
          })
        } else {
          location.reload()
        }
      })
    }
  }

  /**
   * Check if there are any dismissed updates
   */
  public hasDismissedUpdates(): boolean {
    return localStorage.getItem(this.UPDATE_DISMISSED_KEY) !== null
  }

  /**
   * Force show update notification for testing purposes
   */
  public forceShowUpdateNotification(): void {
    this.clearDismissedUpdates()
    this.showUpdateNotification()
  }

  /**
   * Remove loading notification
   */
  private removeLoadingNotification(): void {
    const existing = document.querySelectorAll('[data-update-notification]')
    existing.forEach((el) => el.remove())
  }

  /**
   * Show error notification
   */
  private showErrorNotification(message: string): void {
    // Remove any existing notifications
    this.removeLoadingNotification()

    const notification = document.createElement('div')
    notification.setAttribute('data-update-notification', 'true')
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #f44336;
      color: white;
      padding: 16px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      max-width: 350px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
    `

    notification.textContent = message
    document.body.appendChild(notification)

    // Auto-remove after 5 seconds
    setTimeout(() => {
      notification.remove()
    }, 5000)
  }

  /**
   * Setup error handling to suppress CSP errors during service worker updates
   */
  private setupErrorHandling(): void {
    // Basic service worker error handling
    if (this.swUpdate.isEnabled) {
      this.swUpdate.unrecoverable.subscribe((event) => {
        console.error('Service worker in unrecoverable state:', event.reason)
      })
    }
  }
}
